import React, { ReactElement, useEffect, useState } from "react";
import { Menu, MenuItem, Button, Box, Typography, FormHelperText, useTheme } from "@mui/material";
import { useAppSelector } from "../../../../utils/hooks";
import BeaconCategory from "../../../../components/BeaconCategory";
import ImageLoader from "../../../../components/ImageLoader";
import { Category } from "../../../../types/models/Category";
import { getImageURLCategoryListIcon } from "../../../../utils/utils";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { FormikErrors, FormikTouched } from "formik";
import { AppColor } from "../../../../types/Constants";

interface CategoryDropdownProps {
    onCategorySelect: (category: Category) => void;
    errors: FormikErrors<MakeBeacon>,
    touched: FormikTouched<MakeBeacon>,
    initalCategory?: Category | null;
  }


const CategoryDropdown: React.FC<CategoryDropdownProps> = ({ onCategorySelect, errors, touched, initalCategory }) => {

    const categoriesGroupState = useAppSelector((state) => state.category);
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        setSelectedCategory(initalCategory ?? null);
    }, [initalCategory]);

    const getCategoryMenuItems = () => {
        const items: ReactElement[] = [];
        //Custom Categories
        categoriesGroupState.categories?.map((category) => items.push(
        <MenuItem 
            key={category.Id} 
            value={category.Name}
            onClick={() => handleSelectCategory(category)}
            sx={{ padding: 0, 
                paddingY: 1, 
            }}
        >
            <BeaconCategory 
                category={category.Name} 
                id={category.Id ?? ""}
                fontSize="14px"
                minWidth="204px"
                borderRadius="22px"
                padding="4px 8px"
                justifyContent="left"
                icons={true}
                color={category.Color}
                lastModfied={category.LastModified}
            />
        </MenuItem>
        ));
        return items;
    };
    
    const handleSelectCategory = (category: Category) => {
        setSelectedCategory(category);
        onCategorySelect(category);
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box sx={{
            border: errors.category && touched.category ? "1px solid" : "none",
            borderColor: AppColor.redError,
            borderRadius: 1,
            padding: 2,
            maxWidth: "240px"
        }}>
        <Box sx={{
            backgroundColor: open ? "rgb(238, 238, 238)" : "transparent",
            borderTopLeftRadius: open ? "22px" : 0,
            borderTopRightRadius: open ? "22px" : 0,
            paddingBottom: 3,
            boxShadow: open ? "" : "none",
            display: "inline-block",
            }}
        >
        <Button 
            variant="contained"
            onClick={handleClick}
            endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            sx={{
                fontSize: 14,
                borderRadius: 22,
                minWidth: 220,
                display: "flex",
                justifyContent: "space-between",
                padding: "6px 8px",
                alignItems: "center",
                whiteSpace: 'nowrap',
                color: "#FFF",
                backgroundColor: selectedCategory?.Color.startsWith("#") ? selectedCategory.Color : `#${selectedCategory?.Color}`,
                "&:hover": {
                    backgroundColor: selectedCategory?.Color.startsWith("#") ? selectedCategory.Color : `#${selectedCategory?.Color}`,
                }, 
            }}
        >
            
            <Box sx={{ display: "flex" }}>
            <ImageLoader 
                imageURL={getImageURLCategoryListIcon(selectedCategory?.Id ?? "", selectedCategory?.LastModified ?? "")}
                height="25px"
                width="25px"
                mr="6px"
                borderRadius='50%'
            />
            {selectedCategory?.Name ?? "Select a Category"}
            </Box>
        </Button>
            <FormHelperText sx={{ ml: 2 }} error={((errors.category ? true : undefined) && touched.category)}>
                {errors.category && touched.category ? errors.category : null}
            </FormHelperText>
        </Box>
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
            "aria-labelledby": "basic-button",
            }}
            sx={{
                "& .MuiPaper-root": {
                    marginTop: 1,
                    padding: 2,
                    paddingTop: 0,
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    borderRadius: "0px",
                    
                },
              }}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
        >
            {getCategoryMenuItems()}
        </Menu>
        </Box>
    );
};

export default CategoryDropdown;
